import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { CreateUserRequest, UpdateUserRequest, User as UserData } from '@/api/generated/model/users';
import { useCreateUser, useUpdateUser } from '@/api/generated/users';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Define enums matching API
const ROLES = ['system_admin', 'univ_admin', 'dept_staff', 'viewer'] as const;
const STATUSES = ['active', 'suspended', 'deleted'] as const;

const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email'),
  role: z.enum(ROLES, { required_error: 'Role is required' }),
  departmentCode: z.string().optional(),
  password: z.string().optional(),
  status: z.enum(STATUSES).optional(),
});

type UserFormValues = z.infer<typeof userSchema>;

interface UserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserData | null;
  onSuccess: () => void;
}

export function UserDialog({ open, onOpenChange, user, onSuccess }: Readonly<UserDialogProps>) {
  const isEditing = !!user;
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const loading = createUserMutation.isPending || updateUserMutation.isPending;

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'dept_staff',
      departmentCode: '',
      status: 'active',
      password: '',
    },
  });

  useEffect(() => {
    if (user) {
      // Validate role and status against schema constraints before setting
      const isValidRole = (role: string): role is (typeof ROLES)[number] => {
        return ROLES.includes(role as (typeof ROLES)[number]);
      };
      const isValidStatus = (status: string): status is (typeof STATUSES)[number] => {
        return STATUSES.includes(status as (typeof STATUSES)[number]);
      };

      const role = isValidRole(user.role) ? user.role : 'dept_staff';
      const status = isValidStatus(user.status) ? user.status : 'active';

      form.reset({
        name: user.name,
        email: user.email,
        role: role,
        departmentCode: user.departmentCode || '',
        status: status,
        password: '',
      });
    } else {
      form.reset({
        name: '',
        email: '',
        role: 'dept_staff',
        departmentCode: '',
        status: 'active',
        password: '',
      });
    }
  }, [user, form]);

  const onSubmit = async (data: UserFormValues) => {
    try {
      if (isEditing && user) {
        const payload: UpdateUserRequest = {
          name: data.name,
          role: data.role, // Now strictly typed
          departmentCode: data.departmentCode || undefined,
          status: data.status,
        };
        if (data.password) payload.password = data.password;

        await updateUserMutation.mutateAsync({
          id: user.id,
          data: payload,
        });
      } else {
        const payload: CreateUserRequest = {
          name: data.name,
          email: data.email,
          role: data.role,
          departmentCode: data.departmentCode,
          password: data.password || '', // Backend handles empty password
        };

        await createUserMutation.mutateAsync({
          data: payload,
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save user';
      alert(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit User' : 'Create User'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update user details and permissions.' : 'Add a new administrator to the system.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="john@example.com" {...field} disabled={isEditing} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isEditing && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password (Optional)</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="Auto-generated if empty" {...field} />
                    </FormControl>
                    <FormDescription>Min 8 characters.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="system_admin">System Admin</SelectItem>
                        <SelectItem value="univ_admin">University Admin</SelectItem>
                        <SelectItem value="dept_staff">Dept Staff</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="departmentCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dept Code</FormLabel>
                    <FormControl>
                      <Input placeholder="IT-01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isEditing && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save User'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
